import type { TowerType } from '../core/types';

/**
 * Progressive tower unlocks + the coin Shop.
 *
 * Each Chapter 1 episode (levels 1–5) introduces exactly one tower, shown with a
 * first-start tutorial. The three final towers (blade, bomb, wise) are never
 * granted by an episode: they are one-time purchases in the Shop, paid with
 * persistent Hambi coins, and once owned are available in every episode.
 * A third category — win-granted towers (see `TOWER_GRANTS_ON_WIN`, e.g. the
 * Wizard Tower for beating the Null Nugget) — are granted permanently on
 * first victory over their boss level and are never sold in the Shop.
 * This module is the single source of truth for the schedule and the prices;
 * the HUD shop, the ShopScene, the tutorial trigger, and the balance-proof
 * autoplayer all derive from it.
 */

/** Canonical shop display order (also the full roster). */
export const SHOP_ORDER: TowerType[] = [
  'patty',
  'pickle',
  'cheese',
  'bacon',
  'sauce',
  'blade',
  'bomb',
  'wise',
  'wizard',
  'sniper',
  'darkhambi',
  'ninja',
  'sensei',
  'boxer',
  'gunner',
  'firemage',
  'aquasorcerer',
  'shrekbook',
  'phantom',
  'lightning',
  'spear',
  'bow',
  'capy',
  'generalcapy',
  'galacticcapy',
  'hog',
  'magecapy',
  'minigunner',
  'kingcapy',
  'kinghambi',
  // The Golden Takeout Bag's three (spec 2026-09-26).
  'dagger',
  'leafstorm',
  'samurai',
  // ...and its three more (spec 2026-09-29).
  'masher',
  'grease',
  'ultrahammy',
  // The Origin Event's Dark Shop (spec 2026-10-12): Dark Hambi Coins only.
  'horror',
  'demonic',
  'hamreaper',
  // The Origin Event's Light Shop (spec 2026-10-13): Light Hambi Coins only.
  'healer',
  'banker',
  'angelic',
  // The Fungi Forest's prize (spec 2026-10-27): won on Pathway of the Fungi.
  'goldenshroom',
];

/** Towers each level (episode) introduces, keyed by LevelDef.id. */
export const TOWER_UNLOCKS: Record<number, TowerType[]> = {
  1: ['patty'], // Ch.1 · Ep I
  2: ['pickle'], // Ch.1 · Ep II
  3: ['cheese'], // Ch.1 · Ep III
  4: ['bacon'], // Ch.1 · Ep IV
  5: ['sauce'], // Ch.1 · Ep V
};

/** Level ids whose first start shows a tutorial for the introduced tower. */
export const TUTORIAL_LEVEL_IDS: ReadonlySet<number> = new Set([1, 2, 3, 4, 5]);

/** Towers introduced by a given level (empty if none / unknown level). */
export function towersIntroducedAt(levelId: number): TowerType[] {
  return TOWER_UNLOCKS[levelId] ?? [];
}

/**
 * The OWNERSHIP ROSTER: towers held under the `hambi.owned` key — neither
 * introduced by an episode, nor won from a boss, nor earned by a challenge.
 *
 * These were once the coin Shop's stock, which is where the name comes from.
 * As of the Restaurant (2026-08-14) the Shop sells no towers at all: the ones
 * in `TAKEOUT_BAG_ODDS` are pulled from a Normal Takeout Bag instead, and the
 * rest are legacy — see the note on SHOP_PRICES.
 *
 * DO NOT REMOVE ENTRIES. `ProgressService.ownedTowers()` validates the stored
 * list against this array, so dropping a name here silently confiscates that
 * tower from every save that already bought it.
 */
export type ShopTowerType =
  | 'blade'
  | 'bomb'
  | 'wise'
  | 'sniper'
  | 'darkhambi'
  | 'ninja'
  | 'boxer'
  | 'gunner'
  | 'firemage'
  | 'aquasorcerer'
  | 'phantom'
  | 'lightning'
  | 'spear'
  | 'bow'
  | 'capy'
  | 'generalcapy'
  | 'galacticcapy'
  | 'hog'
  | 'magecapy'
  | 'minigunner'
  | 'kingcapy'
  | 'dagger'
  | 'leafstorm'
  | 'samurai'
  | 'masher'
  | 'grease'
  | 'ultrahammy'
  | 'horror'
  | 'demonic'
  | 'hamreaper'
  | 'healer'
  | 'banker'
  | 'angelic';
export const SHOP_TOWERS: ShopTowerType[] = [
  'blade',
  'bomb',
  'wise',
  'sniper',
  'darkhambi',
  'ninja',
  'boxer',
  'gunner',
  'firemage',
  'aquasorcerer',
  'phantom',
  'lightning',
  'spear',
  'bow',
  // The Capybara Event's capsule Hambis. On the roster so they are owned,
  // equipped and synced like any other — sold on no shelf anywhere.
  'capy',
  'generalcapy',
  'galacticcapy',
  // Update 1.2 put four more in the same capsule, on the same terms.
  'hog',
  'magecapy',
  'minigunner',
  'kingcapy',
  // The Golden Takeout Bag's three (spec 2026-09-26): bag-only, like the
  // Normal bag's five — owned, equipped and synced, sold on no shelf.
  'dagger',
  'leafstorm',
  'samurai',
  // ...and its three more (spec 2026-09-29).
  'masher',
  'grease',
  'ultrahammy',
  // The Origin Event's Dark Shop (spec 2026-10-12): Dark Hambi Coins only.
  'horror',
  'demonic',
  'hamreaper',
  // The Origin Event's Light Shop (spec 2026-10-13): Light Hambi Coins only.
  'healer',
  'banker',
  'angelic',
];

/**
 * The towers the Shop actually SELLS, in page order (three rows per page):
 * page 1 = [blade, bomb, wise], page 2 = [sniper, darkhambi, ninja], which is
 * Chapter-2 gated.
 *
 * This is deliberately NARROWER than SHOP_TOWERS. When the Restaurant arrived
 * it took over Boxer, Gunner, Fire Mage, Aqua Sorcerer and Phantom — those five
 * come out of a Normal Takeout Bag and are sold nowhere. The six here kept
 * their coin prices and their shelf, so no tower is left without a way in.
 *
 * Adding a tower to the bag means REMOVING it from this list, not just adding
 * it to `TAKEOUT_BAG_ODDS` — otherwise it has two sources at once.
 */
export const SHOP_SALE_TOWERS: ShopTowerType[] = [
  'blade',
  'bomb',
  'wise',
  'sniper',
  'darkhambi',
  'ninja',
];

/**
 * The towers The Hub's MARKET sells (spec: 2026-09-02-the-hub-design.md,
 * Revision 6), in shelf order. Bought with the same persistent Hambi coins
 * as the Shop, through the same `purchaseTower`, into the same `hambi.owned`
 * roster — the Market is a second shelf, not a second currency. Like
 * SHOP_SALE_TOWERS this is NARROWER than SHOP_TOWERS: a tower is on exactly
 * one shelf.
 */
export const MARKET_TOWERS: ShopTowerType[] = ['lightning', 'spear', 'bow'];

/**
 * One-time purchase price (persistent Hambi coins) per tower.
 *
 * Every name in the ownership roster has a price, including the five the
 * Restaurant hands out: `purchaseTower` takes a price, and the debug roster and
 * e2e seeds still name them. Only `SHOP_SALE_TOWERS` are actually on sale.
 */
export const SHOP_PRICES: Record<ShopTowerType, number> = {
  blade: 100,
  bomb: 150,
  wise: 250,
  sniper: 300,
  darkhambi: 400,
  ninja: 450,
  boxer: 200,
  gunner: 200,
  firemage: 250,
  aquasorcerer: 250,
  phantom: 400,
  lightning: 400,
  spear: 300,
  bow: 500,
  // Capsule-only, so these prices are never charged — they exist because
  // every roster name needs one (see the note above).
  capy: 100,
  generalcapy: 200,
  galacticcapy: 250,
  hog: 200,
  magecapy: 150,
  minigunner: 250,
  kingcapy: 300,
  // Golden-Bag-only, so never charged either.
  dagger: 170,
  leafstorm: 250,
  samurai: 300,
  masher: 300,
  grease: 300,
  ultrahammy: 400,
  // Dark-Shop-only: bought with Dark Hambi Coins (DARK_SHOP_PRICES), so these
  // coin prices are never charged either.
  horror: 250,
  demonic: 300,
  hamreaper: 450,
  // Light-Shop-only (spec 2026-10-13): bought with Light Hambi Coins
  // (LIGHT_SHOP_PRICES); these are the in-level $ prices, never charged here.
  healer: 250,
  banker: 300,
  angelic: 350,
};

/**
 * Towers that can be permanently Evolved to a 4th tier on the Evolve screen
 * (in its display order — three tubes per page). Evolution is a one-time
 * purchase with persistent Hambi coins, available once Chapter 2 is cleared
 * and only for towers the player has already unlocked.
 */
export const EVOLVE_TOWERS = ['bomb', 'ninja', 'wise', 'sensei', 'cheese', 'patty'] as const;
export type EvolveTowerType = (typeof EVOLVE_TOWERS)[number];

/** One-time Evolution price (persistent Hambi coins) per evolvable tower. */
export const EVOLVE_PRICES: Record<EvolveTowerType, number> = {
  bomb: 200,
  ninja: 500,
  wise: 350,
  sensei: 750,
  cheese: 200,
  patty: 200,
};

/**
 * Towers granted permanently by WINNING a level (not by entering one, and
 * never sold in the Shop). The GameScene win overlay performs the grant.
 */
export const TOWER_GRANTS_ON_WIN: Record<number, TowerType[]> = {
  10: ['wizard'], // Ch.2 · Ep V — beat the Null Nugget
  15: ['sensei'], // Ch.2 · Ep X finale — beat King Nugget-tron
  // Ch.3 · Ep X finale — beat King Ember in The Ember and Flame. Granted on the
  // first win AFTER he existed, so a save that already finished the campaign
  // gets him on its next win of 25 (grantTower is return-once, like claimOrb).
  25: ['kinghambi'],
  // The Fungi Forest, Pathway of the Fungi (spec 2026-10-27): a HUB level, so
  // only a Normal-track win grants it (GameScene's hub branch).
  119: ['goldenshroom'],
};

/**
 * Towers every MENU draws at a fixed tier (spec 2026-10-27): the Golden
 * Shroom's bronze and silver caps are its tiers 1 and 2 on the board, but the
 * Equip screen, the claim modal and the avatar picker always show the GOLD
 * one, so everyone sees it golden. The board keeps the real tier.
 */
export const MENU_TIER: Partial<Record<TowerType, number>> = { goldenshroom: 3 };

/** The texture a menu draws for `tower`: its tier-1 frame unless MENU_TIER says. */
export function menuTowerTexture(tower: TowerType): string {
  return `tower-${tower}-${MENU_TIER[tower] ?? 1}`;
}

/**
 * Towers earned by an in-game CHALLENGE — neither sold, nor introduced by an
 * episode, nor dropped by a boss. Today that is exactly one: Shrekbook Hambi,
 * unlocked by killing 100 Shrekbruits in a single Test Arena wave (see
 * `core/arenaChallenge.ts`). Persisted under its own key so a challenge grant is
 * never confused with a shop purchase or a win reward, and so `resetTowers()`
 * can drop all three categories together.
 */
export const CHALLENGE_TOWERS: TowerType[] = ['shrekbook'];

/** All win-granted towers, flattened (used to validate persisted grants). */
export const WIN_GRANT_TOWERS: TowerType[] = Object.values(TOWER_GRANTS_ON_WIN).flat();

/** Towers granted by winning the given level (empty if none / unknown level). */
export function towersGrantedOnWin(levelId: number): TowerType[] {
  return TOWER_GRANTS_ON_WIN[levelId] ?? [];
}

/**
 * Cumulative available towers, in canonical order: everything the entered
 * episodes granted plus everything bought in the Shop (`owned`).
 */
export function availableTowers(
  enteredLevelIds: Iterable<number>,
  owned: Iterable<TowerType> = [],
): TowerType[] {
  const have = new Set<TowerType>();
  for (const id of enteredLevelIds) {
    for (const t of towersIntroducedAt(id)) have.add(t);
  }
  for (const t of owned) have.add(t);
  return SHOP_ORDER.filter((t) => have.has(t));
}

/**
 * Roster a player has upon reaching `levelId` (grants from all levels ≤ id),
 * plus any shop towers they own.
 */
export function rosterThroughLevel(levelId: number, owned: Iterable<TowerType> = []): TowerType[] {
  const ids: number[] = [];
  for (const key of Object.keys(TOWER_UNLOCKS)) {
    const id = Number(key);
    if (id <= levelId) ids.push(id);
  }
  return availableTowers(ids, owned);
}
